import { EllipsisIcon } from "@do/ui/components/animate-ui/icons/ellispsis";
import { ShimmeringText } from "@do/ui/components/animate-ui/primitives/texts/shimmering";
import { cn } from "@do/ui/lib/utils";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { useMotionEnabled } from "../../../hooks/use-motion-enabled";
import { INSTANT_TRANSITION } from "../../../lib/motion-presets";

type LoaderProps = {
  allowThinking?: boolean;
};

const thinkingSteps = [
  { value: "thinking-1", duration: 3 },
  { value: "thinking-2", duration: 3 },
  { value: "thinking-3" },
];

export const Loader = ({ allowThinking }: LoaderProps) => {
  const motionEnabled = useMotionEnabled();
  const t = useTranslations("do-chat-v2");

  const steps = useMemo(() => {
    return [{ value: "loader", duration: 3 }, ...(allowThinking ? thinkingSteps : [])];
  }, [allowThinking]);

  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (steps.length <= 1) return;

    const step = steps[currentStep];

    if (!step?.duration) return;

    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        const next = prev + 1;
        const nextStep = steps[next];

        if (next >= steps.length - 1 || !nextStep?.duration) {
          clearInterval(interval);
          return steps.length - 1;
        }

        return next;
      });
    }, step.duration * 1000);

    return () => clearInterval(interval);
  }, [currentStep, steps]);

  return (
    <div
      id="loader"
      className="overflow-hidden py-1 h-8"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={steps[currentStep].value}
          transition={motionEnabled ? { duration: 0.3, ease: "easeOut" } : INSTANT_TRANSITION}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
        >
          {steps[currentStep].value === "loader" ? (
            <>
              <EllipsisIcon
                id="loader-ellipsis-icon"
                className={cn(
                  "size-(--loader-ellipsis-icon-size)",
                  "text-(--loader-ellipsis-icon-color)/(--loader-ellipsis-icon-color-opacity)"
                )}
                loop
                animate
                animation="pulse"
                aria-hidden="true"
              />
              <span className="sr-only">{t("chat.messages.loader.loading")}</span>
            </>
          ) : (
            <ShimmeringText
              id={`loader-${steps[currentStep].value}-text`}
              className={cn(
                "font-(family-name:--loader-thinking-font-family)",
                "font-(--loader-thinking-font-weight)!",
                "text-(length:--loader-thinking-font-size)"
              )}
              text={t(`chat.messages.loader.${steps[currentStep].value}`)}
              color="var(--loader-thinking-color)"
              shimmeringColor="var(--loader-thinking-shimmering-color)"
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
